import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api.js';
import {
  normalizeCardRecord,
  computePenaltyTotal,
  totalRedCards,
  visibleYellowCards,
} from '../data/penalties.js';

function computeAwards(teams) {
  const allPlayers = teams.flatMap((t) =>
    t.players.map((p) => ({ ...p, team: t.name, cards: normalizeCardRecord(p.cards) }))
  );
  if (!allPlayers.length) return [];

  const totals = allPlayers.map((p) => {
    const scoredHoles = (p.scores || []).filter((s) => Number(s) > 0);
    const raw = (p.scores || []).reduce((a, s) => a + (Number(s) || 0), 0);
    const penalty = computePenaltyTotal(p.cards);
    const maxHole = scoredHoles.reduce((m, s) => Math.max(m, Number(s)), 0);
    return {
      ...p,
      total: raw + penalty,
      penalty,
      stdev: stdev(scoredHoles),
      maxHoleScore: maxHole,
      playedHoles: scoredHoles.length,
      cardCount: visibleYellowCards(p.cards) + totalRedCards(p.cards),
    };
  });

  // Only players who actually scored on at least one hole qualify.
  const played = totals.filter((p) => p.playedHoles > 0);
  if (!played.length) return [];

  const lowest = played.reduce((a, b) => (a.total < b.total ? a : b));
  const highest = played.reduce((a, b) => (a.total > b.total ? a : b));
  const steady = played.reduce((a, b) => (a.stdev < b.stdev ? a : b));
  // Shanker = single worst HOLE score (not most inconsistent player)
  const shanker = played.reduce((a, b) =>
    a.maxHoleScore >= b.maxHoleScore ? a : b
  );
  const cleanRound = played.filter((p) => p.cardCount === 0);
  const hazardWinner = played.reduce((a, b) =>
    a.cardCount > b.cardCount ? a : b
  );

  const awards = [
    {
      id: 'pro',
      title: 'The Pro',
      icon: '🏆',
      who: `${lowest.name} · ${lowest.team}`,
      desc: `Lowest total — ${lowest.total} strokes`,
    },
    {
      id: 'bogey',
      title: 'Bogey King',
      icon: '🗑️',
      who: `${highest.name} · ${highest.team}`,
      desc: `Highest total — ${highest.total} strokes`,
    },
    {
      id: 'steady',
      title: 'Steady Pour',
      icon: '⚖️',
      who: `${steady.name} · ${steady.team}`,
      desc: `Most consistent — σ ${steady.stdev.toFixed(2)}`,
    },
    {
      id: 'shanker',
      title: 'The Shanker',
      icon: '🌀',
      who: `${shanker.name} · ${shanker.team}`,
      desc: `Worst single hole — ${shanker.maxHoleScore} strokes`,
    },
  ];

  // Only include Clean Round when SOMEONE went clean AND at least one
  // player took cards (otherwise the award is meaningless).
  const anyCards = played.some((p) => p.cardCount > 0);
  if (cleanRound.length && anyCards) {
    awards.push({
      id: 'clean',
      title: 'Clean Round',
      icon: '🛡️',
      who: cleanRound.map((p) => p.name).join(', '),
      desc: 'Zero penalty cards',
    });
  }
  if (hazardWinner.cardCount > 0) {
    awards.push({
      id: 'hazard',
      title: 'The Hazard',
      icon: '⚠️',
      who: `${hazardWinner.name} · ${hazardWinner.team}`,
      desc: `Most penalties — ${hazardWinner.cardCount} cards`,
    });
  }
  return awards;
}

/** Sample standard deviation — more intuitive than raw variance. */
function stdev(arr) {
  if (!arr.length) return 0;
  const mean = arr.reduce((a, b) => a + Number(b), 0) / arr.length;
  const v =
    arr.reduce((a, b) => a + (Number(b) - mean) ** 2, 0) / arr.length;
  return Math.sqrt(v);
}

function Confetti() {
  // Lightweight CSS-only confetti
  const pieces = Array.from({ length: 40 });
  const colors = ['#2D6A3E', '#C99A2E', '#B4452A', '#1E5A8E', '#F4EDD8', '#84A668'];
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 2;
        const dur = 3 + Math.random() * 2;
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-20px',
              width: '10px',
              height: '14px',
              background: color,
              borderRadius: '2px',
              animation: `fall ${dur}s ${delay}s linear infinite`,
              transform: `rotate(${Math.random() * 360}deg)`,
              opacity: 0.8,
            }}
          />
        );
      })}
      <style>{`@keyframes fall { to { transform: translateY(120vh) rotate(720deg); } }`}</style>
    </div>
  );
}

export default function EndGame() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Try the server first; fall back to a local game stored in
    // sessionStorage (custom courses created without a backend).
    const tryLocal = () => {
      try {
        const raw = sessionStorage.getItem(`pubgolf.localGame.${code}`);
        if (raw) {
          setGame(JSON.parse(raw));
          return true;
        }
      } catch {}
      return false;
    };

    if (code?.startsWith('LOCAL-')) {
      tryLocal();
      setLoading(false);
      return;
    }

    api
      .joinGame(code)
      .then((g) => setGame(g?.game || g))
      .catch(() => {
        tryLocal();
      })
      .finally(() => setLoading(false));
  }, [code]);

  const teams = game?.teams || [];

  const standings = useMemo(() => {
    return [...teams]
      .map((t) => ({
        ...t,
        total: t.players.reduce(
          (a, p) =>
            a +
            (p.scores || []).reduce((x, y) => x + (Number(y) || 0), 0) +
            computePenaltyTotal(normalizeCardRecord(p.cards)),
          0
        ),
        par: (game?.holes || []).reduce((a, h) => a + (h.par || 4), 0) *
          t.players.length,
      }))
      .sort((a, b) => a.total - b.total);
  }, [teams, game]);

  const champion = standings[0];
  const awards = useMemo(() => computeAwards(teams), [teams]);

  // Offscreen DOM node that gets rasterized via html2canvas for sharing.
  const winnerCardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const city =
    game?.city || game?.location ||
    (Array.isArray(game?.holes) && (game.holes[0]?.barAddress?.split(',').slice(-2, -1)[0]?.trim() || ''));
  const dateLabel = new Date().toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  /**
   * Rasterize the hidden `.winner-card` into a PNG blob via html2canvas,
   * then either hand it to `navigator.share` (with text + files) or
   * trigger a download as a fallback.
   */
  const share = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const el = winnerCardRef.current;
      if (!el) throw new Error('winner card not mounted');

      // Lazy-load html2canvas on first share — keeps it out of the
      // main bundle for users who never finish a round.
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });

      // Web Share API with files is nicer on mobile — it lets users
      // drop the image straight into Instagram/iMessage/etc.
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png', 0.95)
      );
      if (!blob) throw new Error('toBlob returned null');

      const file = new File([blob], `pubgolf-${champion?.name || 'winner'}.png`, {
        type: 'image/png',
      });

      const shareData = {
        title: 'Pub Golf Champion',
        text: `🏆 ${champion?.name || 'Winner'} — ${champion?.total || 0} strokes\nPub Golf @ pubgolfgame.com`,
      };

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        // Some browsers support share() without files — fall back to
        // text share and also download the PNG so the user has it.
        try {
          await navigator.share(shareData);
        } catch {}
        downloadCanvas(canvas, `pubgolf-${champion?.name || 'winner'}.png`);
      } else {
        downloadCanvas(canvas, `pubgolf-${champion?.name || 'winner'}.png`);
      }
    } catch (err) {
      console.warn('share failed', err);
    } finally {
      setSharing(false);
    }
  };

  const downloadCanvas = (canvas, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="screen center">
        <div className="loading-state">
          <div className="spinner" />
          <span className="loading-state-label">Loading round…</span>
        </div>
      </div>
    );
  }

  if (!game || !champion) {
    return (
      <div className="screen">
        <div className="empty-state">
          <div className="icon">🏆</div>
          <h3>Round not found</h3>
          <p>This round has either been cleared or never existed.</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" style={{ position: 'relative' }}>
      <Confetti />
      <div className="end-hero">
        <div className="trophy">🏆</div>
        <div className="label">Champion</div>
        <h1>{champion?.name || '—'}</h1>
        <div className="score">{champion?.total || 0} STROKES</div>
        <div className="sub">
          {(champion?.total || 0) - (champion?.par || 0) > 0
            ? `+${(champion?.total || 0) - (champion?.par || 0)} over par`
            : 'Under par'}
        </div>
      </div>

      <div className="u-label" style={{ marginBottom: '0.75rem' }}>
        Final Standings
      </div>
      <div className="standings">
        {standings.map((t, i) => (
          <div
            key={t.id || i}
            className={'standings-row' + (i === 0 ? ' winner' : '')}
          >
            <div className="rank">{i + 1}</div>
            <div className="grow">
              <div className="name">{t.name}</div>
              <div className="members">
                {t.players.map((p) => p.name).join(', ')}
              </div>
            </div>
            <div>
              <div className="score">{t.total}</div>
              <div className="diff">
                {t.total - t.par > 0 ? `+${t.total - t.par}` : t.total - t.par}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="u-label" style={{ marginBottom: '0.75rem' }}>
        Awards
      </div>
      <div>
        {awards.map((a) => (
          <div className="award-card" key={a.id}>
            <div className="icon">{a.icon}</div>
            <div className="body">
              <h4>{a.title}</h4>
              <div className="who">{a.who}</div>
              <div className="desc">{a.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="stack-sm" style={{ marginTop: '1.5rem' }}>
        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={share}
          disabled={sharing}
        >
          {sharing ? 'Generating…' : '⇪ Share Winner'}
        </button>
        <button
          className="btn btn-ghost btn-block"
          onClick={() => navigate('/stories/new', { state: { sessionCode: code } })}
        >
          📸 Post to Stories
        </button>
        <button className="btn btn-ghost btn-block" onClick={() => window.print()}>
          ↗ Export Scorecard
        </button>
        <button
          className="btn btn-ghost btn-block"
          onClick={() => navigate('/create')}
        >
          Play Again
        </button>
      </div>

      {/* Offscreen winner card used by html2canvas for the share image */}
      <div className="winner-card-wrap" aria-hidden="true">
        <div className="winner-card" ref={winnerCardRef}>
          <div className="winner-card-bg" />
          <div className="winner-card-content">
            <div className="winner-card-logo">PUB GOLF</div>
            <div className="winner-card-eyebrow">CHAMPION</div>
            <div className="winner-card-name">
              <span className="winner-card-crown">👑</span>
              {champion?.name || '—'}
            </div>
            <div className="winner-card-score">
              {champion?.total || 0} STROKES
            </div>
            <div className="winner-card-meta">
              {city && <span>{city}</span>}
              {city && <span className="dot">·</span>}
              <span>{dateLabel}</span>
            </div>
            <div className="winner-card-tagline">
              A Distinguished Game for Distinguished Drinkers
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
